import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useState } from "react";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { useCurrency, CURRENCIES } from "@/context/CurrencyContext";
import { Menu, X, ShoppingBag, User } from "lucide-react";

const links = [
  { to: "/", label: "Home", id: "home" },
  { to: "/masterclass", label: "Masterclasses & Workshops", id: "masterclass" },
  { to: "/bookings", label: "Bookings", id: "bookings" },
  { to: "/sacred-tools", label: "Sacred Tools", id: "sacred-tools" },
  { to: "/circle", label: "Join the Circle", id: "circle" },
  { to: "/library", label: "Library", id: "library" },
  { to: "/about", label: "About", id: "about" },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const { totalItems, setIsOpen } = useCart();
  const { currency, setCurrency } = useCurrency();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  // Funnel mode: on the masterclass landing page, hide secondary links to keep focus on the CTA
  const funnel = pathname === "/masterclass";
  const visibleLinks = funnel ? links.filter((l) => ["home", "masterclass", "sacred-tools"].includes(l.id)) : links;

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleLogin = () => {
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <header
      className="fixed top-0 left-0 right-0 z-50 glass-strong"
      data-testid="site-navbar"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-4 flex items-center justify-between gap-6">
        <Link to="/" className="flex items-center gap-2.5 group" data-testid="nav-brand">
          <motion.div
            initial={{ opacity: 0, scale: 0.6, rotate: -25 }}
            animate={{ opacity: 1, scale: 1, rotate: 0 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.05 }}
            className="logo-mark-wrap relative"
          >
            <img
              src="/logo-mark.png"
              alt="Just be you logo"
              className="logo-mark h-10 w-10 md:h-11 md:w-11 rounded-full object-cover ring-1 ring-lapis/10 shadow-sm transition-transform duration-500 ease-out group-hover:scale-105 group-hover:-rotate-3"
              data-testid="nav-logo-mark"
            />
            <span aria-hidden className="logo-mark-glow" />
          </motion.div>
          <span className="flex items-baseline gap-2 whitespace-nowrap">
            <span className="font-serif italic text-2xl tracking-tight text-lapis">Just be you</span>
            <span className="overline text-[0.6rem] text-sage hidden sm:inline">by Mehjabin</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {visibleLinks.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-link-${l.id}`}
              className={({ isActive }) =>
                `text-sm tracking-wide font-medium whitespace-nowrap transition-colors duration-300 ${
                  isActive ? "text-lapis" : "text-ink/60 hover:text-lapis"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-4">
          <div className="flex items-center bg-white/60 rounded-full border border-lapis/15 p-0.5" data-testid="currency-toggle">
            {CURRENCIES.map((c) => (
              <button
                key={c.code}
                onClick={() => setCurrency(c.code)}
                className={`px-2 py-1 rounded-full text-xs font-semibold whitespace-nowrap transition ${currency === c.code ? "bg-lapis text-canvas" : "text-ink/60 hover:text-lapis"}`}
                data-testid={`currency-${c.code.toLowerCase()}`}
                aria-label={`Prices in ${c.code}`}
                title={c.code}
              >
                {c.symbol}
              </button>
            ))}
          </div>
          <button
            onClick={() => setIsOpen(true)}
            className="relative text-lapis hover:text-lapis-deep transition"
            data-testid="nav-cart-btn"
            aria-label="Cart"
          >
            <ShoppingBag size={20} strokeWidth={1.5} />
            {totalItems > 0 && (
              <span
                className="absolute -top-2 -right-2 bg-peach-deep text-white text-[10px] rounded-full w-5 h-5 flex items-center justify-center font-semibold"
                data-testid="nav-cart-count"
              >
                {totalItems}
              </span>
            )}
          </button>

          {user ? (
            <>
              {user.role === "admin" && (
                <button
                  onClick={() => navigate("/admin")}
                  className="text-xs uppercase tracking-[0.18em] text-sage-deep hover:text-lapis font-semibold"
                  data-testid="nav-admin-btn"
                >
                  Admin
                </button>
              )}
              <button
                onClick={() => navigate("/dashboard")}
                className="flex items-center gap-2 text-ink/80 hover:text-lapis transition-colors"
                data-testid="nav-user-menu"
              >
                {user.picture ? (
                  <img src={user.picture} alt="" className="w-8 h-8 rounded-full border border-lapis/30" />
                ) : (
                  <User size={18} />
                )}
                <span className="font-serif italic">{user.name?.split(" ")[0]}</span>
              </button>
              <button onClick={logout} className="text-xs uppercase tracking-widest text-ink/50 hover:text-lapis" data-testid="nav-logout-btn">
                Sign out
              </button>
            </>
          ) : (
            <button onClick={handleLogin} className="btn-soft" data-testid="nav-login-btn">
              Enter the circle
            </button>
          )}
        </div>

        <div className="lg:hidden flex items-center gap-4">
          <button
            onClick={() => setIsOpen(true)}
            className="relative text-lapis"
            data-testid="nav-cart-btn-mobile"
            aria-label="Cart"
          >
            <ShoppingBag size={22} strokeWidth={1.5} />
            {totalItems > 0 && (
              <span className="absolute -top-2 -right-2 bg-peach-deep text-white text-[10px] rounded-full w-5 h-5 flex items-center justify-center font-semibold">
                {totalItems}
              </span>
            )}
          </button>
          <button onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle" className="text-lapis">
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-lapis/10 px-6 py-8 space-y-6 bg-canvas/95" data-testid="nav-mobile-menu">
          {visibleLinks.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `block text-lg font-serif italic ${isActive ? "text-lapis" : "text-ink/80"}`
              }
              data-testid={`nav-mobile-link-${l.id}`}
            >
              {l.label}
            </NavLink>
          ))}
          <div className="divider-soft" />
          <div className="flex items-center gap-2" data-testid="currency-toggle-mobile">
            {CURRENCIES.map((c) => (
              <button
                key={c.code}
                onClick={() => setCurrency(c.code)}
                className={`px-4 py-2 rounded-full text-sm font-semibold border transition ${currency === c.code ? "bg-lapis text-canvas border-lapis" : "border-lapis/25 text-ink/70"}`}
                data-testid={`currency-mobile-${c.code.toLowerCase()}`}
              >
                {c.symbol}
              </button>
            ))}
          </div>
          {user ? (
            <div className="space-y-4">
              {user.role === "admin" && (
                <button onClick={() => { setOpen(false); navigate("/admin"); }} className="btn-soft w-full justify-center" data-testid="nav-mobile-admin-btn">
                  Admin
                </button>
              )}
              <button onClick={() => { setOpen(false); navigate("/dashboard"); }} className="btn-soft w-full justify-center" data-testid="nav-mobile-dashboard-btn">
                My Space
              </button>
              <button onClick={() => { setOpen(false); logout(); }} className="text-xs uppercase tracking-widest text-ink/50">Sign out</button>
            </div>
          ) : (
            <button onClick={handleLogin} className="btn-primary w-full justify-center" data-testid="nav-mobile-login-btn">
              Enter the circle
            </button>
          )}
        </div>
      )}
    </header>
  );
}
